import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import {
  getMissionDocumentPreviewUrl,
  guessViewerKind,
  guessViewerKindFromMime,
} from "utils/missionDocuments";

function MissionDocumentViewerDialog({
  open,
  onClose,
  document: doc,
  idToken,
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [viewerKind, setViewerKind] = useState("other");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const revokeRef = useRef(null);

  const fileName = doc?.documentName || doc?.name || "Document";

  useEffect(() => {
    if (!open || !doc || !idToken) {
      if (revokeRef.current) {
        revokeRef.current();
        revokeRef.current = null;
      }
      setPreviewUrl(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    if (revokeRef.current) {
      revokeRef.current();
      revokeRef.current = null;
    }
    setPreviewUrl(null);

    const pathKind = guessViewerKind(
      doc?.documentPath || doc?.key || fileName,
    );

    getMissionDocumentPreviewUrl(doc, idToken)
      .then(({ previewUrl: url, revoke, contentType }) => {
        if (cancelled) {
          revoke();
          return;
        }
        revokeRef.current = revoke;
        setPreviewUrl(url);
        const mimeKind = guessViewerKindFromMime(contentType);
        setViewerKind(
          mimeKind !== "other" ? mimeKind : pathKind,
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Failed to load document");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (revokeRef.current) {
        revokeRef.current();
        revokeRef.current = null;
      }
    };
  }, [open, doc, idToken]);

  const renderViewer = () => {
    if (!previewUrl) return null;

    if (viewerKind === "pdf") {
      return (
        <SoftBox
          component="iframe"
          src={`${previewUrl}#toolbar=1&navpanes=0`}
          title={fileName}
          sx={{
            width: "100%",
            height: { xs: "60vh", md: "75vh" },
            border: 0,
            borderRadius: 1,
            bgcolor: "grey.100",
          }}
        />
      );
    }

    if (viewerKind === "image") {
      return (
        <SoftBox
          component="img"
          src={previewUrl}
          alt={fileName}
          sx={{
            maxWidth: "100%",
            maxHeight: { xs: "60vh", md: "75vh" },
            display: "block",
            margin: "0 auto",
            borderRadius: 1,
          }}
        />
      );
    }

    if (viewerKind === "video") {
      return (
        <SoftBox
          component="video"
          src={previewUrl}
          controls
          sx={{
            width: "100%",
            maxHeight: { xs: "60vh", md: "75vh" },
            borderRadius: 1,
          }}
        />
      );
    }

    return (
      <SoftBox py={4} textAlign="center">
        <SoftTypography variant="body2" color="text" mb={2}>
          Preview is not available for this file type. Open or download the
          file instead.
        </SoftTypography>
        <SoftButton
          variant="gradient"
          color="info"
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in new tab
        </SoftButton>
      </SoftBox>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: "92vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <SoftTypography variant="h6" fontWeight="bold" sx={{ pr: 2 }}>
          {fileName}
        </SoftTypography>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {loading && (
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight={280}
            gap={1}
          >
            <CircularProgress size={28} />
            <SoftTypography variant="body2" color="text">
              Loading document…
            </SoftTypography>
          </SoftBox>
        )}
        {error && !loading && (
          <Alert severity="error">{error}</Alert>
        )}
        {!loading && !error && renderViewer()}
      </DialogContent>
    </Dialog>
  );
}

MissionDocumentViewerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  document: PropTypes.object,
  idToken: PropTypes.string,
};

export default MissionDocumentViewerDialog;
